<template>
  <v-dialog
    v-model="isVisible"
    fullscreen
    persistent
  >
    <v-card>
      <v-toolbar
        flat
        height="80"
        max-height="80"
      >
        <v-avatar :color="$t('Delivery.DeliveryStaffAddDialog.Toolbar.Color')">
          <v-icon dark>
            shopping_cart
          </v-icon>
        </v-avatar>

        <v-toolbar-title class="pl-3">
          <div class="body-3">
            {{ $t('Delivery.DeliveryStaffInfo.Button.AddOrder.Title') }}
          </div>
          <div class="text-caption">
            {{ $t('Delivery.DeliveryUser.Settings.CanAddOrder.Subtitle') }}
          </div>
        </v-toolbar-title>

        <v-spacer />

        <v-btn
          icon
          @click="onCancelButtonClick"
        >
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-divider />

      <v-card-text style="height: calc(100vh - 150px);">
        <h4>Scan Order Barcode</h4>
        <div class="d-flex text-center align-center justify-center fill-height">
          <barcode-scanner
            :debug="debug"
            :formats="formats"
            :highlight-css="highlightCss"
            :mask-css="maskCss"
            :rate="rateValue + rateSuffix"
            :scanning.sync="scanning"
            :source="source"
            class="barcode-scanner"
            @bcs-started="isScanning = true"
            @bcs-stopped="isScanning = false"
            @bcs-scanned="scanned"
            @bcs-error="showError"
          />
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import BarcodeScanner from '@undecaf/vue-barcode-scanner'
import {BarcodeDetectorPolyfill} from '@undecaf/barcode-detector-polyfill'
import DialogVisible from '@/mixins/dialogVisible'
import {clone} from '@/lib/helper/helper'

const DEFAULT_FORMATS = ['code_128']

/**
 * Returns the native BarcodeDetector if available, or undefined.
 */
function getNativeBarcodeDetector() {
  try {
    window.BarcodeDetector.getSupportedFormats()
    return window.BarcodeDetector
    // eslint-disable-next-line no-empty
  } catch {
  }
}

export default {
  name: 'BarcodeScannerDialog',

  components: {
    BarcodeScanner
  },

  mixins: [DialogVisible],

  data() {
    return {
      nativeBarcodeDetector      : getNativeBarcodeDetector(),
      isBarcodeDetectorPolyfilled: !getNativeBarcodeDetector(),
      sessionBarcodesScanned     : [],
      sourceOption               : null,
      source                     : null,
      file                       : null,
      mediaUrl                   : null,
      mediaResponse              : null,
      enteringUrl                : false,
      scanning                   : true,
      singleScans                : false,
      supportedFormats           : [],
      formats                    : [],
      rateValue                  : 20,
      rateSuffix                 : '/s',
      maskCss                    : null,
      highlightCss               : undefined,
      debug                      : false,
      isScanning                 : false,
      error                      : null
    }
  },

  computed: {
    frontCamera() {
      return {facingMode: 'user'}
    },

    backCamera() {
      return {facingMode: 'environment'}
    }
  },

  watch: {
    sourceOption(option, prevOption) {
      this.sessionBarcodesScanned = []
      this.error = null

      if (option || (option === this.frontCamera) || (option === this.backCamera)) {
        // Convert video options to a MediaStream from a camera
        navigator.mediaDevices.getUserMedia({
          audio: false,
          video: option
        })
          .then(stream => {
            this.source = stream
          })
          .catch(error => this.errorHandler(error))
      }
    }
  },

  mounted() {
    this.sourceOption = this.backCamera
    this.selectBarcodeDetector()
  },

  methods: {
    scanned(barcodes) {
      this.error = null
      if (!barcodes.length) return

      if (this.singleScans) this.scanning = false

      const barcodesNotScannedInSession = barcodes.filter(scannedBarcode => !this.sessionBarcodesScanned.find(sessionBarcode => sessionBarcode.rawValue === scannedBarcode.rawValue))

      if (barcodesNotScannedInSession.length) this.$emit('barcodes', clone(barcodesNotScannedInSession))

      this.sessionBarcodesScanned = [...this.sessionBarcodesScanned, ...barcodesNotScannedInSession]
    },

    showError(error) {
      this.error = error
    },

    async selectBarcodeDetector() {
      window.BarcodeDetector = this.isBarcodeDetectorPolyfilled ? BarcodeDetectorPolyfill : this.nativeBarcodeDetector
      this.supportedFormats = [...await window.BarcodeDetector.getSupportedFormats()].sort()
      this.formats = DEFAULT_FORMATS.filter(f => this.supportedFormats.includes(f))
    },

    errorHandler(error) {
      this.error = `${error}. The resource may have been blocked by the server's CORS policy.`
      this.sourceOption = null
    },

    onCancelButtonClick() {
      this.closeDialog()
    }
  }
}
</script>

<style scoped>
h4 {
  text-align: center;
  margin: 1rem 0;
}

h4 > span {
  margin-right: 2em;
}

img.info {
  vertical-align: text-bottom;
  margin-left: 0.5em;
}

.row {
  max-width: 80rem;
}

.error {
  color: crimson;
}

.barcode-scanner {
  display: flex;
  justify-content: center;
}

table {
  table-layout: fixed;
}

th, td {
  text-align: center;
}

td > pre {
  margin: 0;
  text-align: left;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow: hidden;
}

td > input[type='checkbox'] {
  margin: 0;
  pointer-events: none;
}

#source-container {
  position: relative;
}

#source-container input {
  position: absolute;
  top: 0;
  left: 0;
}

#formats {
  font-family: monospace, monospace;
  height: inherit;
  overflow-y: auto;
}

#formats ~ .note {
  margin-top: -1.5rem;
  margin-bottom: 1.5rem;
}

input[type='radio'] + span {
  margin: 0 0.5em;
}

input[type='file'] {
  display: none;
}

img.info + input {
  margin-left: 1em;
}
</style>
